import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";

import { useAlert } from "react-alert";

import {
  AuctionType,
  AuctionCreate,
} from "src/adminModel/auctions";

import Color from "src/utils/colors";


// =====================================================
// CONSTANTS
// =====================================================

const AUCTION_STATUS = [
  {
    value: 0,
    label: "Previous auction",
  },
  {
    value: 1,
    label: "Upcoming auction",
  },
  {
    value: 2,
    label: "Ongoing auction",
  },
];

const INITIAL_VALUES = {
  auction_name: "",
  auction_descriptions: "",
  catalog_download: "",
  catalog_url: "",
  date_start: "",
  date_end: "",
  auctionType: "",
  auction_status: "",
  bp: "",
};


// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",

    "& .MuiTypography-h5": {
      color: Color.green,
    },
  },

  card: {
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
  },

  header: {
    padding: theme.spacing(3),
    backgroundColor: "#fff",
  },

  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#222",
  },

  subtitle: {
    marginTop: 5,
    fontSize: 13,
    color: "#777",
  },

  content: {
    padding: theme.spacing(3),
  },

  section: {
    marginBottom: theme.spacing(4),
  },

  section: {
    marginBottom: 0,
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2.5),
  },

  sectionNumber: {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: "50%",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    backgroundColor: "#222",
    color: "#fff",

    fontSize: 13,
    fontWeight: 600,

    marginRight: 12,
  },

  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#222",
  },

  sectionDescription: {
    marginTop: 2,
    fontSize: 12,
    color: "#888",
  },

  field: {
    "& .MuiInputLabel-outlined": {
      fontSize: 14,
    },

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },

  premiumField: {
    "& .MuiInputBase-input": {
      fontWeight: 600,
    },
  },

  actionBar: {
    padding: theme.spacing(2, 3),
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: "#fafafa",
  },

  saveButton: {
    minWidth: 140,
    height: 40,
    fontWeight: 600,
    textTransform: "none",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));


// =====================================================
// COMPONENT
// =====================================================

const Create = ({
  className,
  ...rest
}) => {
  const classes = useStyles();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);

  const [values, setValues] = useState(
    INITIAL_VALUES
  );

  const [errors, setErrors] = useState({});


  // ===================================================
  // HANDLE CHANGE
  // ===================================================

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };


  // ===================================================
  // VALIDATION
  // ===================================================

  const validate = () => {
    const next = {};

    if (!values.auction_name.trim()) {
      next.auction_name = "Required.";
    }

    if (!values.date_start) {
      next.date_start = "Required.";
    }

    if (!values.date_end) {
      next.date_end = "Required.";
    }

    if (!values.auctionType) {
      next.auctionType = "Required.";
    }

    if (values.auction_status === "") {
      next.auction_status = "Required.";
    }

    if (
      values.date_start &&
      values.date_end &&
      values.date_end < values.date_start
    ) {
      next.date_end =
        "End date is before the start date.";
    }

    const bp = parseFloat(values.bp);

    if (values.bp === "") {
      next.bp = "Required.";
    } else if (Number.isNaN(bp)) {
      next.bp = "Must be a number.";
    } else if (bp < 0 || bp > 1) {
      next.bp =
        "Use a decimal: 0.15 for 15%.";
    }

    setErrors(next);

    return Object.keys(next).length === 0;
  };


  // ===================================================
  // SUBMIT
  // ===================================================

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      alert.error(
        "Please check the highlighted fields."
      );

      return;
    }

    setLoading(true);

    try {
      const result = await AuctionCreate({
        ...values,
        bp: parseFloat(values.bp),
      });

      if (result.success === false) {
        alert.error(result.errors);
      } else {
        alert.success(result.msg);

        setValues({
          ...INITIAL_VALUES,
        });
      }

    } catch (error) {
      console.error(
        "AuctionCreate failed:",
        error
      );

      alert.error(
        "Could not save. Please check your connection and try again."
      );

    } finally {
      setLoading(false);
    }
  };


  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Box
      className={clsx(
        classes.root,
        className
      )}
      {...rest}
    >

      <form
        onSubmit={handleFormSubmit}
        autoComplete="off"
      >

        <Card className={classes.card}>

          {/* =========================================
              HEADER
          ========================================= */}

         


     


          {/* =========================================
              CONTENT
          ========================================= */}

          <CardContent
            className={classes.content}
          >

            {/* =======================================
                1. AUCTION INFORMATION
            ======================================= */}

            <Box className={classes.section}>

              <Box
                className={classes.sectionHeader}
              >

                <Box
                  className={classes.sectionNumber}
                >
                  1
                </Box>

                <Box>

                  <Typography
                    className={classes.sectionTitle}
                  >
                    Auction Information
                  </Typography>

                  <Typography
                    className={classes.sectionDescription}
                  >
                    Basic information about the auction
                  </Typography>

                </Box>

              </Box>


              <Grid
                container
                spacing={3}
              >

                {/* Auction Name */}

                <Grid
                  item
                  xs={12}
                >

                  <TextField
                    fullWidth
                    required
                    label="Auction Name"
                    name="auction_name"
                    variant="outlined"
                    value={
                      values.auction_name
                    }
                    onChange={
                      handleChange
                    }
                    error={Boolean(
                      errors.auction_name
                    )}
                    helperText={
                      errors.auction_name
                    }
                    className={
                      classes.field
                    }
                  />

                </Grid>


                {/* Description */}

                <Grid
                  item
                  xs={12}
                >

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    name="auction_descriptions"
                    variant="outlined"
                    value={
                      values.auction_descriptions
                    }
                    onChange={
                      handleChange
                    }
                    className={
                      classes.field
                    }
                  />

                </Grid>

              </Grid>

            </Box>


            <Divider />


            {/* =======================================
                2. AUCTION SCHEDULE
            ======================================= */}

            <Box
              className={classes.section}
              style={{
                marginTop: 32,
              }}
            >

              <Box
                className={classes.sectionHeader}
              >

                <Box
                  className={classes.sectionNumber}
                >
                  2
                </Box>

                <Box>

                  <Typography
                    className={classes.sectionTitle}
                  >
                    Auction Schedule
                  </Typography>

                  <Typography
                    className={classes.sectionDescription}
                  >
                    Set the auction dates and status
                  </Typography>

                </Box>

              </Box>


              <Grid
                container
                spacing={3}
              >

                {/* Start Date */}

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    required
                    label="Start Date"
                    name="date_start"
                    type="date"
                    variant="outlined"
                    value={
                      values.date_start
                    }
                    onChange={
                      handleChange
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={Boolean(
                      errors.date_start
                    )}
                    helperText={
                      errors.date_start
                    }
                    className={
                      classes.field
                    }
                  />

                </Grid>


                {/* End Date */}

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    required
                    label="End Date"
                    name="date_end"
                    type="date"
                    variant="outlined"
                    value={
                      values.date_end
                    }
                    onChange={
                      handleChange
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={Boolean(
                      errors.date_end
                    )}
                    helperText={
                      errors.date_end
                    }
                    className={
                      classes.field
                    }
                  />

                </Grid>


                {/* Auction Type */}

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    required
                    select
                    label="Auction Type"
                    name="auctionType"
                    variant="outlined"
                    value={
                      values.auctionType
                    }
                    onChange={
                      handleChange
                    }
                    error={Boolean(
                      errors.auctionType
                    )}
                    helperText={
                      errors.auctionType
                    }
                    className={
                      classes.field
                    }
                  >

                    {AuctionType.map(
                      (val) => (
                        <MenuItem
                          key={val.id}
                          value={val.value}
                        >
                          {val.name}
                        </MenuItem>
                      )
                    )}

                  </TextField>

                </Grid>


                {/* Auction Status */}

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    required
                    select
                    label="Auction Status"
                    name="auction_status"
                    variant="outlined"
                    value={
                      values.auction_status
                    }
                    onChange={
                      handleChange
                    }
                    error={Boolean(
                      errors.auction_status
                    )}
                    helperText={
                      errors.auction_status
                    }
                    className={
                      classes.field
                    }
                  >

                    {AUCTION_STATUS.map(
                      (status) => (
                        <MenuItem
                          key={status.value}
                          value={status.value}
                        >
                          {status.label}
                        </MenuItem>
                      )
                    )}

                  </TextField>

                </Grid>

              </Grid>

            </Box>


            <Divider />


            {/* =======================================
                3. CATALOG
            ======================================= */}

            <Box
              className={classes.section}
              style={{
                marginTop: 32,
              }}
            >

              <Box
                className={classes.sectionHeader}
              >

                <Box
                  className={classes.sectionNumber}
                >
                  3
                </Box>

                <Box>

                  <Typography
                    className={classes.sectionTitle}
                  >
                    Catalog
                  </Typography>

                  <Typography
                    className={classes.sectionDescription}
                  >
                    Add links for the auction catalog
                  </Typography>

                </Box>

              </Box>


              <Grid
                container
                spacing={3}
              >

                {/* Catalog URL */}

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    label="Catalog URL"
                    name="catalog_url"
                    variant="outlined"
                    value={
                      values.catalog_url
                    }
                    onChange={
                      handleChange
                    }
                    className={
                      classes.field
                    }
                  />

                </Grid>


                {/* Catalog Download */}

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    label="Catalog Download"
                    name="catalog_download"
                    variant="outlined"
                    value={
                      values.catalog_download
                    }
                    onChange={
                      handleChange
                    }
                    className={
                      classes.field
                    }
                  />

                </Grid>

              </Grid>

            </Box>


            <Divider />


            {/* =======================================
                4. AUCTION SETTINGS
            ======================================= */}

            <Box
              style={{
                marginTop: 32,
              }}
            >

              <Box
                className={classes.sectionHeader}
              >

                <Box
                  className={classes.sectionNumber}
                >
                  4
                </Box>

                <Box>

                  <Typography
                    className={classes.sectionTitle}
                  >
                    Auction Settings
                  </Typography>

                  <Typography
                    className={classes.sectionDescription}
                  >
                    Configure the buyer's premium
                  </Typography>

                </Box>

              </Box>


              <Grid
                container
                spacing={3}
              >

                <Grid
                  item
                  xs={12}
                  md={6}
                >

                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Buyer's Premium"
                    name="bp"
                    variant="outlined"
                    value={values.bp}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: "0.0001",
                      min: 0,
                      max: 1,
                    }}
                    error={Boolean(
                      errors.bp
                    )}
                    helperText={
                      errors.bp ||
                      "Decimal value. Example: 0.15 = 15%"
                    }
                    className={clsx(
                      classes.field,
                      classes.premiumField
                    )}
                  />

                </Grid>

              </Grid>

            </Box>

          </CardContent>


          {/* =========================================
              ACTIONS
          ========================================= */}

          <Divider />

          <Box
            className={classes.actionBar}
          >

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              className={classes.saveButton}
            >
              {loading
                ? "Saving..."
                : "Save Auction"}
            </Button>

          </Box>

        </Card>

      </form>


      {/* ===========================================
          LOADING
      =========================================== */}

      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </Box>
  );
};


// =====================================================
// PROP TYPES
// =====================================================

Create.propTypes = {
  className: PropTypes.string,
};


export default Create;